import { useState, type KeyboardEvent, type ReactNode } from 'react';
import {
  flexRender, getCoreRowModel, getSortedRowModel, useReactTable,
  type ColumnDef, type SortingState, type Row, type RowData,
} from '@tanstack/react-table';
import {
  ContentSwitcher, DataTableSkeleton, Switch, Table, TableBody, TableCell, TableContainer, TableHead, TableHeader, TableRow,
} from '@carbon/react';
import { EmptyState, ErrorState } from './states';
import { datCheDo, useCheDoDs } from './cheDoDs';
import { rowActivates } from './rowActivates';

// Chon hien Danh sach / The, chi hien tren dien thoai (styles.scss); ap dung cho moi bang
export function CheDoNut() {
  const cheDo = useCheDoDs();
  return (
    <div className="che-do-ds">
      <ContentSwitcher size="sm" selectedIndex={cheDo === 'the' ? 1 : 0} onChange={(e) => datCheDo(e.name === 'the' ? 'the' : 'danh-sach')}>
        <Switch name="danh-sach" text="Danh sách" />
        <Switch name="the" text="Thẻ" />
      </ContentSwitcher>
    </div>
  );
}

declare module '@tanstack/react-table' {
  interface ColumnMeta<TData extends RowData, TValue> {
    /** class on the header and every cell of the column (e.g. cot-dinh: pinned left in the phone list mode) */
    className?: string;
  }
}

/** a paged list sorted by the server: the header asks for that order across every page instead of reordering one page */
export type ServerSort = { cols: Record<string, string>; value: string; onChange: (value: string) => void };

// Carbon's header text promises ascending / unsort; a server order is one direction and stays until another is chosen
const serverSortText = (_id: string, a?: { header?: ReactNode; isSortHeader?: boolean }) =>
  (a?.isSortHeader ? 'Đang sắp theo ' : 'Sắp theo ') + String(a?.header ?? '') + ', cao trước, trên toàn danh sách';

// TanStack Table state + Carbon table markup. Sorting is client-side on the loaded rows unless the page passes
// serverSort (client 2026-10-09: tapping Số dư on the player list reordered only the 50 loaded rows, so most
// accounts with a balance never showed); server filters live in the page toolbar.
export function DataTable<T>({
  columns, data, loading, error, onRetry, empty, onRowClick, title, description, toolbar, footer, rowKey, serverSort,
}: {
  columns: ColumnDef<T, any>[];
  data: T[] | undefined;
  loading?: boolean;
  error?: unknown;
  onRetry?: () => void;
  empty: { title: string; body?: ReactNode; action?: ReactNode };
  onRowClick?: (row: T) => void;
  title?: string;
  description?: string;
  toolbar?: ReactNode;
  footer?: ReactNode;
  rowKey?: (row: T) => string;
  /** column id -> server sort value; only those columns sort, always descending like the server orders */
  serverSort?: ServerSort;
}) {
  const [localSorting, setSorting] = useState<SortingState>([]);
  const sorting: SortingState = serverSort
    ? Object.entries(serverSort.cols).filter(([, v]) => v === serverSort.value).map(([id]) => ({ id, desc: true }))
    : localSorting;
  const table = useReactTable({
    data: data ?? [],
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    manualSorting: !!serverSort,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getRowId: rowKey ? (r) => rowKey(r) : undefined,
  });

  if (loading && !data) {
    return <DataTableSkeleton columnCount={Math.min(columns.length, 8)} rowCount={8} showHeader={!!title} showToolbar={false} />;
  }

  return (
    <TableContainer title={title} description={description}>
      {toolbar}
      {!error && data && data.length > 0 ? <CheDoNut /> : null}
      {error ? <ErrorState error={error} onRetry={onRetry} /> : null}
      {!error && data && data.length === 0 ? <EmptyState {...empty} /> : null}
      {!error && data && data.length > 0 ? (
        <Table size="sm" useZebraStyles={false} aria-busy={loading ? 'true' : 'false'}>
          <TableHead>
            {table.getHeaderGroups().map((hg) => (
              <TableRow key={hg.id}>
                {hg.headers.map((h) => {
                  const server = serverSort?.cols[h.column.id];
                  const canSort = serverSort ? server !== undefined : h.column.getCanSort() && h.column.columnDef.enableSorting !== false;
                  const dir = h.column.getIsSorted();
                  const sortClick = server !== undefined ? () => serverSort!.onChange(server) : h.column.getToggleSortingHandler();
                  return (
                    <TableHeader
                      key={h.id}
                      colSpan={h.colSpan}
                      isSortable={canSort}
                      sortDirection={dir === 'asc' ? 'ASC' : dir === 'desc' ? 'DESC' : 'NONE'}
                      isSortHeader={!!dir}
                      onClick={canSort ? sortClick : undefined}
                      translateWithId={server !== undefined ? serverSortText : undefined}
                      className={h.column.columnDef.meta?.className}
                    >
                      {h.isPlaceholder ? null : flexRender(h.column.columnDef.header, h.getContext())}
                    </TableHeader>
                  );
                })}
              </TableRow>
            ))}
          </TableHead>
          <TableBody>
            {table.getRowModel().rows.map((row: Row<T>) => (
              <TableRow key={row.id} className={onRowClick ? 'row--click' : undefined}
                onClick={onRowClick ? () => onRowClick(row.original) : undefined}
                tabIndex={onRowClick ? 0 : undefined}
                onKeyDown={onRowClick ? (e: KeyboardEvent<HTMLElement>) => {
                  if (rowActivates(e.key, e.target, e.currentTarget)) { e.preventDefault(); onRowClick(row.original); }
                } : undefined}>
                {row.getVisibleCells().map((c) => {
                  const head = c.column.columnDef.header;
                  const label = typeof head === 'string' ? head : '';
                  return <TableCell key={c.id} data-label={label} className={c.column.columnDef.meta?.className}>{flexRender(c.column.columnDef.cell, c.getContext())}</TableCell>;
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : null}
      {footer && data && data.length > 0 ? <div className="table-foot">{footer}</div> : null}
    </TableContainer>
  );
}
